import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { useIntegrationTierConfig } from './useIntegrationTierConfig'
import { isOperationalConnectionStatus, type ConnectionMetadata, type IntegrationProviderId } from '../types/integration'
import type { StorageConnectionMetadata } from '../types/schema'
import { isNoSignInPreviewActive } from '../debug'

export type IntegrationAvailability = Record<IntegrationProviderId, boolean>
const unavailable: IntegrationAvailability = {
  G_DRIVE: false,
  NOTION: false,
  JIRA: false,
  GOOGLE_CALENDAR: false,
  GOOGLE_SHEETS: false,
  SLACK: false,
  FIGMA: false,
  ZOOM: false,
  DROPBOX: false,
  GITHUB: false,
  GENERIC_WEBHOOK: false,
  STAYS_PMS: false,
  CLINICS_CONNECTORS: false,
}

const asLinkHubConnection = (document: StorageConnectionMetadata): ConnectionMetadata | null => {
  if (!['G_DRIVE', 'NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GENERIC_WEBHOOK'].includes(document.provider)) return null
  const providerId = document.provider as IntegrationProviderId
  const status = document.status === 'ACTIVE' ? 'ACTIVE' : document.status === 'ACTION_REQUIRED' || document.status === 'ERROR' ? 'EXPIRED' : 'DISCONNECTED'
  return {
    id: document.id, workspaceId: document.workspaceId, providerId, status,
    lastSync: document.lastSyncAt as ConnectionMetadata['lastSync'], tokenRef: `storage_secrets/${document.id}`,
    scopes: [], createdAt: document.createdAt as ConnectionMetadata['createdAt'], runtime: document.runtime,
    runtimeStatus: document.runtimeStatus, runtimeSummary: document.runtimeSummary,
    runtimeUpdatedAt: document.runtimeUpdatedAt as ConnectionMetadata['runtimeUpdatedAt'],
  }
}

export const useIntegrationSubscriptions = (workspaceId: string, enabled = true) => {
  const tierConfig = useIntegrationTierConfig()
  const isPreview = isNoSignInPreviewActive()
  const [subscribedConnections, setSubscribedConnections] = useState<{
    workspaceId: string
    connections: ConnectionMetadata[]
    error: string | null
  } | null>(null)

  useEffect(() => {
    if (!workspaceId || !enabled || isPreview) return
    let active = true
    const currentWorkspaceId = workspaceId
    const unsubscribe = onSnapshot(
      query(collection(firestoreDb, 'storage_connections'), where('workspaceId', '==', currentWorkspaceId)),
      (snapshot) => {
        if (!active) return
        setSubscribedConnections({
          workspaceId: currentWorkspaceId,
          connections: snapshot.docs
            .map((document) => asLinkHubConnection({ id: document.id, ...document.data() } as StorageConnectionMetadata))
            .filter((connection): connection is ConnectionMetadata => connection !== null),
          error: null,
        })
      },
      (snapshotError) => {
        if (!active) return
        setSubscribedConnections({
          workspaceId: currentWorkspaceId,
          connections: [],
          error: snapshotError.message,
        })
      },
    )
    return () => {
      active = false
      unsubscribe()
    }
  }, [enabled, isPreview, workspaceId])

  const availability = useMemo(() => ({ ...unavailable, ...tierConfig.config.flags }), [tierConfig.config.flags])
  const currentAvailability = (!workspaceId || !enabled) ? unavailable : availability

  const isSubscribed = Boolean(workspaceId && enabled && (isPreview || subscribedConnections?.workspaceId === workspaceId))
  const currentConnections = useMemo(() => {
    if (!workspaceId || !enabled || isPreview) return []
    if (subscribedConnections?.workspaceId === workspaceId) {
      return subscribedConnections.connections
    }
    return []
  }, [enabled, isPreview, subscribedConnections, workspaceId])

  const connectionsByProvider = new Map<IntegrationProviderId, ConnectionMetadata>()
  currentConnections.forEach((connection) => {
    const current = connectionsByProvider.get(connection.providerId)
    if (!current || (!isOperationalConnectionStatus(current.status) && isOperationalConnectionStatus(connection.status)) || (current.status === 'CONNECTED' && connection.status === 'ACTIVE')) {
      connectionsByProvider.set(connection.providerId, connection)
    }
  })

  const connectionError = (workspaceId && enabled && !isPreview && subscribedConnections?.workspaceId === workspaceId)
    ? subscribedConnections.error
    : null

  return {
    connections: currentConnections,
    connectionsByProvider,
    availability: currentAvailability,
    loading: enabled && (!isSubscribed || tierConfig.loading),
    error: connectionError ?? tierConfig.error,
  }
}
